import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2, Receipt, MailCheck } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { useApi, errMsg } from "@/lib/api";
import { downloadInvoice } from "@/lib/pdf";

const money = (c, cur) => `$${(c / 100).toFixed(2)} ${cur.toUpperCase()}`;

const InvoiceRow = ({ inv }) => {
  const [busy, setBusy] = useState(false);
  const get = async () => {
    setBusy(true);
    try { await downloadInvoice(inv); } catch { toast.error("Could not create the invoice PDF"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-xl border border-paper-border bg-white p-5" data-testid={`invoice-row-${inv.number}`}>
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-paper-subtle text-thread"><Receipt className="h-5 w-5" /></span>
      <div className="min-w-[200px] flex-1">
        <p className="font-semibold text-ink">{inv.description}</p>
        <p className="font-mono text-[11px] uppercase tracking-wider text-taupe">
          {inv.number} · {inv.date_display}{inv.emailed && <span className="ml-2 inline-flex items-center gap-1 normal-case text-green-700"><MailCheck className="h-3 w-3" />emailed</span>}
        </p>
      </div>
      <div className="text-right">
        <p className="font-serif text-xl text-ink" data-testid={`invoice-total-${inv.number}`}>{money(inv.total_cents, inv.currency)}</p>
        <p className="text-xs text-taupe">{inv.gst_cents > 0 ? `incl. GST ${money(inv.gst_cents, inv.currency)}` : "No GST"}</p>
      </div>
      <button className="btn-ink" onClick={get} disabled={busy} data-testid={`invoice-download-${inv.number}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
      </button>
    </div>
  );
};

export default function Invoices() {
  const api = useApi();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/invoices").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, [api]);

  return (
    <div className="min-h-screen bg-paper">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:py-14" data-testid="invoices-page">
        <p className="eyebrow">Your purchases</p>
        <h1 className="mt-2 text-4xl text-ink sm:text-5xl">Invoices</h1>
        <p className="mt-3 max-w-xl text-ink-soft">A tax invoice is emailed after every purchase. You can download a copy here any time.</p>
        {!list ? <Loader2 className="mt-10 h-6 w-6 animate-spin text-thread" /> : list.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-paper-border bg-white/60 p-10 text-center" data-testid="invoices-empty">
            <p className="text-ink-soft">No purchases yet. Invoices appear here after you unlock a download.</p>
            <Link to="/app" className="btn-thread mt-5">Back to my documents</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3">{list.map((inv) => <InvoiceRow key={inv.number} inv={inv} />)}</div>
        )}
      </main>
    </div>
  );
}
